import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Hls from 'hls.js'
import { useQuery } from '@tanstack/react-query'
import { MediaService, MediaMetadata, Skip } from '@mc/edge-api'
import type { PlaybackError } from './PlaybackError'
import { type Source, type Track } from './Video'
import { attachHlsErrorRecovery } from './hlsErrorRecovery'
import { useDeterminePlaybackEngine } from './useDeterminePlaybackEngine'

const HLS_TYPES = ['application/x-mpegURL']
const MP4_TYPES = ['video/mp4', 'application/dash+xml']

type VideoSource = {
  poster: string
  sources: Source[]
  tracks: Track[]
  skips: Skip[]
  duration: number | null
}

interface LoadedVideo {
  tracks: Track[]
  poster: string
  source: Source | null
  /**
   * Authoritative duration in whole seconds from the media API, or `null` when
   * unavailable. The element's own `duration` over-reports the asset by up to a
   * second on HLS (MCCERTS-2828), so prefer this for anything user-visible.
   */
  metadataDuration: number | null
  hls: Hls | null
  decoder: 'hls.js' | 'native-hls' | 'non-hls'
  skips: Skip[]
  /**
   * Rebuild the playback pipeline for the same source — signed URLs are
   * stable for 24h, so a source-keyed effect can never replace a failed
   * instance.
   */
  reload: () => void
}

/**
 * Loads the video by the given `mediaUuid` or `explicitSource` to the element in the given `videoRef`
 * The `videoRef`'s `loadedmetadata` event will fire when the duration and dimensions are loaded
 */
export const useLoadVideoById = ({
  mediaUuid,
  explicitSource,
  videoRef,
  onFatalError,
}: {
  mediaUuid: string | undefined
  explicitSource: Source | null | undefined
  videoRef: React.RefObject<HTMLVideoElement | null>
  /** Fatal hls.js error that bounded recovery couldn't fix */
  onFatalError?: (error: PlaybackError) => void
}): LoadedVideo => {
  const [hls, setHls] = useState<Hls | null>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)

  const onFatalErrorRef = useRef(onFatalError)
  onFatalErrorRef.current = onFatalError

  // Position to carry across a pipeline rebuild of the *same* media — a
  // rotated signed URL or reload(). Keyed by media identity so a genuinely
  // different video never inherits it.
  const resumeRef = useRef<{ mediaKey: string; time: number } | null>(null)
  const mediaKey = mediaUuid ?? explicitSource?.src

  const { data: edgeData } = useQuery({
    queryKey: ['edge-media-api', mediaUuid],
    queryFn: () => MediaService.getMediaMetadata({ uuid: mediaUuid as string }),
    enabled: !!mediaUuid && !explicitSource,
    select: presentMediaApiResponse,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  const {
    tracks,
    poster,
    sources,
    skips = [],
    duration: metadataDuration,
  } = edgeData ?? {
    tracks: [],
    sources: [],
    poster: '',
    source: null,
    skips: [],
    duration: null,
  }

  const { source, decoder } = useDetermineSource({
    videoRef,
    sources,
    explicitSource,
  })

  useEffect(() => {
    const video = videoRef.current

    if (!video || !source?.src) return () => {}

    const resume = resumeRef.current
    resumeRef.current = null
    const resumeAt =
      resume && resume.mediaKey === mediaKey && resume.time > 0
        ? resume.time
        : undefined

    const rememberPosition = () => {
      if (mediaKey && video.currentTime > 0) {
        resumeRef.current = { mediaKey, time: video.currentTime }
      }
    }

    if (decoder !== 'hls.js') {
      video.src = source.src
      video.load()

      const restorePosition = () => {
        if (resumeAt !== undefined) video.currentTime = resumeAt
      }
      video.addEventListener('loadedmetadata', restorePosition, { once: true })

      return () => {
        rememberPosition()
        video.removeEventListener('loadedmetadata', restorePosition)
        video.removeAttribute('src')
        video.load()
      }
    } else {
      const hlsInstance = new Hls({
        renderTextTracksNatively: false,
        capLevelToPlayerSize: true,
        capLevelOnFPSDrop: true,
        backBufferLength: 60,
        autoStartLoad: resumeAt === undefined,
      })

      setHls(hlsInstance)

      const teardown = initializeHls(
        hlsInstance,
        source.src,
        video,
        (error) => onFatalErrorRef.current?.(error),
        resumeAt,
      )

      return () => {
        rememberPosition()
        teardown()
      }
    }
  }, [videoRef, source?.src, decoder, loadAttempt, mediaKey])

  const reload = useCallback(() => setLoadAttempt((n) => n + 1), [])

  return {
    tracks,
    poster,
    decoder,
    source: source || null,
    hls,
    skips,
    metadataDuration,
    reload,
  }
}

const presentMediaApiResponse = ({
  textTracks = [],
  sources = [],
  poster = '',
  skips = [],
  duration,
}: MediaMetadata): VideoSource => ({
  tracks: textTracks.map(({ src, srclang, kind, label }) => ({
    src,
    srcLang: srclang,
    label,
    kind,
  })) as Track[],
  sources: sources.filter(({ src, type }) => src && type) as Source[],
  poster,
  skips, // Will be populated from backend when available
  // Media API reports milliseconds; floor to match how the player renders time.
  duration: duration ? Math.floor(duration / 1000) : null,
})

const findSourceByType = (sources: Source[], validTypes: string[]) =>
  sources.find(({ type }) => validTypes.some((t) => type.includes(t)))

const useDetermineSource = ({
  videoRef,
  sources,
  explicitSource,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>
  sources: Source[]
  explicitSource: Source | null | undefined
}) => {
  const engine = useDeterminePlaybackEngine(videoRef.current)

  const desiredType = engine === 'non-hls' ? MP4_TYPES : HLS_TYPES
  const loadedSource = useMemo(
    () => findSourceByType(sources, desiredType),
    [desiredType, sources],
  )

  if (explicitSource) {
    return {
      source: explicitSource,
      decoder: findSourceByType([explicitSource], HLS_TYPES)
        ? engine
        : 'non-hls',
    }
  }

  return { source: loadedSource, decoder: engine }
}

const initializeHls = (
  hls: Hls,
  source: string,
  video: HTMLVideoElement,
  onFatalError: (error: PlaybackError) => void,
  startPosition?: number,
) => {
  const handleMediaAttached = () => {
    hls.loadSource(source)
  }
  // Only registered when the instance was created with autoStartLoad: false.
  const handleManifestParsed = () => {
    if (startPosition !== undefined) hls.startLoad(startPosition)
  }

  const detachErrorRecovery = attachHlsErrorRecovery(hls, onFatalError)
  hls.once(Hls.Events.MEDIA_ATTACHED, handleMediaAttached)
  if (startPosition !== undefined) {
    hls.once(Hls.Events.MANIFEST_PARSED, handleManifestParsed)
  }

  hls.attachMedia(video)
  return () => {
    detachErrorRecovery()
    hls.off(Hls.Events.MEDIA_ATTACHED, handleMediaAttached)
    hls.off(Hls.Events.MANIFEST_PARSED, handleManifestParsed)
    hls.detachMedia()
    hls.destroy()
  }
}
